import type { ChatConnectionState, ChatProvider, IntegrationCompany } from '@web/data/repositories';
import { RequestError } from '@voll-intelligence/domain';
import { ToneChip, useAuthz, type AppIconName } from '@web/ui/core';
import { currentLocale, t } from '@web/i18n';

export const integrationsKey = ['admin', 'integrations'] as const;
export const companiesKey = (q: string) => [...integrationsKey, 'companies', q] as const;
export const pendingKey = [...integrationsKey, 'pending'] as const;

export const CHAT_TOOLS: readonly ChatProvider[] = ['teams', 'slack'];

/** What the caller may do here: only renders — the server checks the same permissions. */
export function useIntegrationsAccess(): { canConnect: boolean; canApprove: boolean } {
  const authz = useAuthz();
  return {
    canConnect: authz.has('kanban:integrations:connect'),
    canApprove: authz.has('kanban:integrations:approve'),
  };
}

/** What differs between the chat tools on screen; everything else is shared. */
export const TOOL: Record<ChatProvider, { icon: AppIconName; name: () => string }> = {
  teams: { icon: 'teams', name: () => t('integrations.teams.name') },
  slack: { icon: 'slack', name: () => t('integrations.slack.name') },
};

export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat(currentLocale(), { dateStyle: 'short' }).format(new Date(iso));
}

export function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat(currentLocale(), {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(iso));
}

export type ChipState = ChatConnectionState['status'] | 'expired';

export function chipStateOfLink(link: { status: string; expired: boolean }): ChipState {
  if (link.status === 'review') return 'review';
  return link.expired ? 'expired' : 'pending';
}

export function chipStateOf(state: ChatConnectionState): ChipState {
  if (state.status === 'pending') return chipStateOfLink(state.link);
  return state.status;
}

/** A tool is "taken" for the company while connected or waiting on the client's IT. */
export function isBusy(state: ChatConnectionState): boolean {
  return state.status === 'connected' || state.status === 'pending' || state.status === 'review';
}

/** The tool the company list summarizes: the one in use, else none. */
export function activeTool(company: IntegrationCompany): ChatProvider | null {
  return CHAT_TOOLS.find((p) => isBusy(company[p])) ?? null;
}

export function ChatStatusChip({ state }: Readonly<{ state: ChipState }>): React.JSX.Element {
  switch (state) {
    case 'connected':
      return (
        <ToneChip tone="green" icon="check-circle">
          {t('integrations.status.connected')}
        </ToneChip>
      );
    case 'pending':
      return (
        <ToneChip tone="amber" icon="clock-countdown">
          {t('integrations.status.pending')}
        </ToneChip>
      );
    case 'expired':
      return (
        <ToneChip tone="gray" icon="clock-countdown">
          {t('integrations.status.expired')}
        </ToneChip>
      );
    case 'review':
      return (
        <ToneChip tone="amber" icon="flag">
          {t('integrations.status.review')}
        </ToneChip>
      );
    case 'unavailable':
      return <ToneChip tone="gray">{t('integrations.status.unavailable')}</ToneChip>;
    case 'none':
    default:
      return (
        <ToneChip tone="gray" icon="x-circle">
          {t('integrations.status.none')}
        </ToneChip>
      );
  }
}

// The kanban answers in English; staff see the reason in their language. 409
// carries `details.reason` when the company already uses the other chat tool.
export function integrationErrorMessage(err: unknown): string {
  if (err instanceof RequestError) {
    const body = err.details as { details?: { reason?: unknown } } | undefined;
    if (err.status === 409 && body?.details?.reason === 'other_chat_tool') {
      return t('integrations.toast.otherChatTool');
    }
    if (err.status === 409) return t('integrations.toast.conflict');
    if (err.status === 404) return t('integrations.toast.notFound');
    if (err.status === 503) return t('integrations.toast.unavailable');
  }
  return t('integrations.toast.error');
}
